/**
 * A segmented 8px bar with a key (DESIGN.md "Severity meter", "MFA bar"):
 * segments sized by value and split by 1px gaps, then a key of glyph or
 * swatch, count and word. Severity segments take the severity tones (Coral
 * for critical only) and, with `glyph`, the severity shapes in the key; the
 * MFA bar is `strong` (without MFA) beside `off` (with MFA). The bar is
 * decorative; the key carries the numbers for assistive tech.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { IconSeverityCrit, IconSeverityHigh, IconSeverityLow, IconSeverityMed, type IconComponent } from './icons'
import { TONE_BG, TONE_TEXT } from './tones'
import type { MeterTone, SeverityLevel } from './types'

export interface MeterSegment {
  id: string
  value: number
  tone: MeterTone
  /** The key's word after the count ("critical", "without MFA"). */
  label: string
  /** Lead the key entry with the severity shape (severity tones only) instead of a swatch. */
  glyph?: boolean
}

export interface SegmentedMeterProps {
  segments: MeterSegment[]
  /** The helper line above the bar ("Failing controls by severity"). */
  caption: ReactNode
  className?: string
}

const SHAPE: Partial<Record<MeterTone, IconComponent>> = {
  crit: IconSeverityCrit,
  high: IconSeverityHigh,
  med: IconSeverityMed,
  low: IconSeverityLow,
}

const isSeverity = (tone: MeterTone): tone is SeverityLevel => tone in SHAPE

export function SegmentedMeter({ segments, caption, className }: SegmentedMeterProps) {
  const shown = segments.filter((s) => s.value > 0)
  return (
    <div className={clsx('min-w-0', className)}>
      <p className="mb-2 text-helper text-text-helper">{caption}</p>
      <div aria-hidden="true" className={clsx('flex h-2 gap-px', !shown.length && 'bg-layer-accent')}>
        {shown.map((s) => (
          <span key={s.id} className={TONE_BG[s.tone]} style={{ flex: s.value }} />
        ))}
      </div>
      <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-helper text-text-secondary">
        {segments.map((s) => {
          const Shape = s.glyph && isSeverity(s.tone) ? SHAPE[s.tone] : undefined
          return (
            <li key={s.id} className="inline-flex items-center gap-1">
              {Shape ? (
                <Shape size={12} className={TONE_TEXT[s.tone]} />
              ) : (
                <span aria-hidden="true" className={clsx('mr-0.5 h-2.5 w-2.5 flex-none', TONE_BG[s.tone])} />
              )}
              <span className="tabular-nums">
                {s.value.toLocaleString('en-US')} {s.label}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default SegmentedMeter
